import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/AppShell";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Loader } from "@/components/kit";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ProfessionalDashboard from "@/pages/ProfessionalDashboard";
import OrgDashboard from "@/pages/OrgDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import KridaPass from "@/pages/KridaPass";
import KridaWork from "@/pages/KridaWork";
import Marketplace from "@/pages/Marketplace";
import Ops from "@/pages/Ops";
import TournamentDetail from "@/pages/TournamentDetail";
import Skills from "@/pages/Skills";
import Pay from "@/pages/Pay";
import Intelligence from "@/pages/Intelligence";
import Directory from "@/pages/Directory";
import Verifications from "@/pages/Verifications";
import AuditLogs from "@/pages/AuditLogs";
import Availability from "@/pages/Availability";
import Disputes from "@/pages/Disputes";
import Messages from "@/pages/Messages";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen bg-krida-bg"><Loader label="Authenticating" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return <AppShell><ErrorBoundary>{children}</ErrorBoundary></AppShell>;
}

function RoleDashboard() {
  const { user } = useAuth();
  if (user?.role === "organisation") return <OrgDashboard />;
  if (user?.role === "admin") return <AdminDashboard />;
  return <ProfessionalDashboard />;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen bg-krida-bg"><Loader /></div>;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/app" element={<Protected><RoleDashboard /></Protected>} />
          <Route path="/app/pass" element={<Protected roles={["professional"]}><KridaPass /></Protected>} />
          <Route path="/app/work" element={<Protected roles={["professional", "organisation"]}><KridaWork /></Protected>} />
          <Route path="/app/marketplace" element={<Protected roles={["organisation", "admin"]}><Marketplace /></Protected>} />
          <Route path="/app/ops" element={<Protected roles={["organisation", "admin"]}><Ops /></Protected>} />
          <Route path="/app/ops/:tid" element={<Protected roles={["organisation", "admin"]}><TournamentDetail /></Protected>} />
          <Route path="/app/skills" element={<Protected roles={["professional", "organisation", "admin"]}><Skills /></Protected>} />
          <Route path="/app/pay" element={<Protected roles={["professional", "organisation", "admin"]}><Pay /></Protected>} />
          <Route path="/app/intelligence" element={<Protected><Intelligence /></Protected>} />
          <Route path="/app/directory" element={<Protected roles={["organisation", "admin"]}><Directory /></Protected>} />
          <Route path="/app/verifications" element={<Protected roles={["admin"]}><Verifications /></Protected>} />
          <Route path="/app/availability" element={<Protected roles={["professional"]}><Availability /></Protected>} />
          <Route path="/app/disputes" element={<Protected><Disputes /></Protected>} />
          <Route path="/app/messages" element={<Protected roles={["professional", "organisation"]}><Messages /></Protected>} />
          <Route path="/app/audit" element={<Protected roles={["admin"]}><AuditLogs /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors theme="dark" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
